import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { db, storage } from '../firebase/firebase';
import { Box, Button, TextField, Typography, List, ListItem, IconButton, MenuItem, Select, FormControl, InputLabel, LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../components/Auth';
import RecordContentModal from '../components/RecordContentModal';

const MentorPage = () => {
  const [courses, setCourses] = useState([]);
  const [newCourse, setNewCourse] = useState({ name: '', description: '', videoUrl: '', price: '', author: '', image: '', curriculum: [] });
  const [newTopic, setNewTopic] = useState({ mainTopic: '', subParts: [] });
  const [newSubPart, setNewSubPart] = useState({ title: '', content: [] });
  const [newContent, setNewContent] = useState({ type: 'video', url: '' });
  const [contentType, setContentType] = useState('video');
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
  const [isTopicAdded, setIsTopicAdded] = useState(false);
  const [isSubTopicAdded, setIsSubTopicAdded] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const user = getCurrentUser();

  useEffect(() => {
    const fetchCourses = async () => {
      const coursesCollection = collection(db, 'courses');
      const courseSnapshot = await getDocs(coursesCollection);
      const courseList = courseSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCourses(courseList.filter(course => course.mentorId === user.uid));
    };
    fetchCourses();
  }, [user.uid]);

  const handleAddCourse = async () => {
    if (!newCourse.name || !newCourse.price || !newCourse.author || !newCourse.image) {
      alert("Please fill in all required fields.");
      return;
    }

    if (isNaN(newCourse.price)) {
      alert("Price must be a valid number.");
      return;
    }

    const coursesCollection = collection(db, 'courses');
    await addDoc(coursesCollection, { ...newCourse, mentorId: user.uid });
    setNewCourse({ name: '', description: '', videoUrl: '', price: '', author: '', image: '', curriculum: [] });
    setSuccessMessage('Course added successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
    const courseSnapshot = await getDocs(coursesCollection);
    setCourses(courseSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(course => course.mentorId === user.uid));
  };

  const handleUpdateCourse = async (id, updatedCourse) => {
    const courseDoc = doc(db, 'courses', id);
    await updateDoc(courseDoc, updatedCourse);
    setCourses(courses.map(course => (course.id === id ? { ...course, ...updatedCourse } : course)));
  };

  const handleAddTopic = () => {
    setNewCourse({ ...newCourse, curriculum: [...newCourse.curriculum, newTopic] });
    setNewTopic({ mainTopic: '', subParts: [] });
    setIsTopicAdded(true);
    setIsSubTopicAdded(false);
  };

  const handleAddSubPart = () => {
    const updatedCurriculum = newCourse.curriculum.map((topic, index) => {
      if (index === newCourse.curriculum.length - 1) {
        return { ...topic, subParts: [...topic.subParts, newSubPart] };
      }
      return topic;
    });
    setNewCourse({ ...newCourse, curriculum: updatedCurriculum });
    setNewSubPart({ title: '', content: [] });
    setIsSubTopicAdded(true);
  };

  const handleAddContent = async () => {
    if (newContent.type === 'firebaseVideo' && !newContent.file) {
      alert("Please select a file for the video.");
      return;
    }

    if (newContent.type !== 'firebaseVideo' && !newContent.url) {
      alert("Press Finish Adding New Course in case of screen recording; else please provide a URL for the content. ");
      return;
    }

    if (newContent.type === 'firebaseVideo' && newContent.file) {
      if (newContent.file.size > process.env.REACT_APP_MAX_VIDEO_SIZE) {
        alert("Video file size must be less than 15 MB.");
        return;
      }
      const storageRef = ref(storage, `videos/${newCourse.name}/${newContent.file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, newContent.file);

      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        }, 
        (error) => {
          console.error('Upload failed', error);
        }, 
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          const contentWithoutFile = { ...newContent, url };
          delete contentWithoutFile.file;
          finalizeContentAddition(contentWithoutFile);
        }
      );
    } else if (newContent.type === 'pdf' && newContent.file) {
      if (newContent.file.size > process.env.REACT_APP_MAX_PDF_SIZE) {
        alert("PDF file size must be less than 5 MB.");
        return;
      }
      const storageRef = ref(storage, `pdfs/${newCourse.name}/${newContent.file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, newContent.file);

      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        }, 
        (error) => {
          console.error('Upload failed', error);
        }, 
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          const contentWithoutFile = { ...newContent, url };
          delete contentWithoutFile.file;
          finalizeContentAddition(contentWithoutFile);
        }
      );
    } else {
      finalizeContentAddition(newContent);
    }
  };

  const finalizeContentAddition = (content) => {
    const updatedCurriculum = newCourse.curriculum.map((topic, index) => {
      if (index === newCourse.curriculum.length - 1) {
        const updatedSubParts = topic.subParts.map((subPart, subIndex) => {
          if (subIndex === topic.subParts.length - 1) {
            return { ...subPart, content: [...subPart.content, content] };
          }
          return subPart;
        });
        return { ...topic, subParts: updatedSubParts };
      }
      return topic;
    });
    setNewCourse({ ...newCourse, curriculum: updatedCurriculum });
    setNewContent({ type: 'video', url: '' });
    setUploadProgress(null);
  };

  const handleMakeTest = (courseName) => {
    const formattedCourseName = courseName.toLowerCase().replace(/\s+/g, '-');
    navigate(`/mentor/create-test/${formattedCourseName}`);
  };

  const handleUploadSignature = async (courseName, file) => {
    const validTypes = ['image/png'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid PNG image.');
      return;
    }
    const formattedCourseName = courseName
      .toLowerCase()
      .replace(/[^a-zA-Z]/g, '-') // Remove all non-alphabet characters (including colons) and replace with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
      .replace(/^-|-$/g, '') // Remove leading and trailing hyphens
      .trim(); // Remove any leading or trailing spaces

    const storageRef = ref(storage, `signatures/${formattedCourseName}/signature.png`);
    await uploadBytes(storageRef, file);
    console.log('Signature uploaded successfully');
  };

  const handleSignatureChange = (courseName) => (event) => {
    const file = event.target.files[0];
    if (file) {
      handleUploadSignature(courseName, file);
    }
  };

  const handleRecordedContent = (content) => {
    finalizeContentAddition(content);
    setContentType('video');
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Typography variant="h4" gutterBottom>Mentor Dashboard</Typography>
      
      <Typography variant="h6">Add New Course</Typography>
      <TextField label="Course Name" value={newCourse.name} onChange={(e) => setNewCourse({ ...newCourse, name: e.target.value })} fullWidth />
      <TextField label="Description" value={newCourse.description} onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })} fullWidth />
      <TextField label="Video URL (Optional)" value={newCourse.videoUrl} onChange={(e) => setNewCourse({ ...newCourse, videoUrl: e.target.value })} fullWidth />
      <TextField label="Price (Enter Value without Symbol)" value={newCourse.price} onChange={(e) => setNewCourse({ ...newCourse, price: e.target.value })} fullWidth />
      <TextField label="Author" value={newCourse.author} onChange={(e) => setNewCourse({ ...newCourse, author: e.target.value })} fullWidth />
      <TextField label="Thumbnail Image URL (Copy Image Address of any Image from Google)" value={newCourse.image} onChange={(e) => setNewCourse({ ...newCourse, image: e.target.value })} fullWidth />
      
      <Typography variant="h6" sx={{ mt: 4 }}>Add Curriculum</Typography>
      <TextField label="Topic" value={newTopic.mainTopic} onChange={(e) => setNewTopic({ ...newTopic, mainTopic: e.target.value })} fullWidth />
      <Button variant="contained" onClick={handleAddTopic}>Add Topic</Button>
      
      <Typography variant="h6" sx={{ mt: 4 }}>Add Subtopic</Typography>
      <TextField label="Subtopic" value={newSubPart.title} disabled={!isTopicAdded} onChange={(e) => setNewSubPart({ ...newSubPart, title: e.target.value })} fullWidth />
      <Button variant="contained" onClick={handleAddSubPart} disabled={!isTopicAdded}>Add Subtopic</Button>
      
      <Typography variant="h6" sx={{ mt: 4 }}>Add Content</Typography>
      <FormControl fullWidth>
        <InputLabel>Content Type</InputLabel>
        <Select
          value={contentType}
          onChange={(e) => setContentType(e.target.value)}
          disabled={!isSubTopicAdded}
        >
          <MenuItem value="video">Video (YouTube)</MenuItem>
          <MenuItem value="pdf">PDF</MenuItem>
          <MenuItem value="firebaseVideo">Video (Firebase Storage)</MenuItem>
          <MenuItem value="record">Record Content (Audio/Video)</MenuItem>
        </Select>
      </FormControl>
      {contentType === 'record' ? (
        <RecordContentModal
          open={true}
          onClose={() => setContentType('video')}
          onContentRecorded={handleRecordedContent}
          courseName={newCourse.name}
        />
      ) : contentType === 'firebaseVideo' || contentType === 'pdf' ? (
        <>
          <TextField
            type="file"
            onChange={(e) => setNewContent({ ...newContent, type: contentType, file: e.target.files[0] })}
            fullWidth
          />
          {uploadProgress !== null && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              {uploadProgress === 100 && <Typography variant="body2" color="success.main">Upload complete!</Typography>}
            </Box>
          )}
        </>
      ) : (
        <TextField
          label="Content URL"
          value={newContent.url}
          onChange={(e) => setNewContent({ ...newContent, type: contentType, url: e.target.value })}
          fullWidth
          disabled={!isSubTopicAdded}
        />
      )}
      <Button variant="contained" onClick={handleAddContent} disabled={!isSubTopicAdded}>Upload Content</Button>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start'}}>
        <Button variant="contained" onClick={handleAddCourse} sx={{ mt: 2, backgroundColor: '#1F7523FF', '&:hover': { backgroundColor: '#1C491EFF' } }}>Finish Adding New Course</Button>
        {successMessage && <Typography variant="body2" color="success.main" sx={{ ml: 2 }}>{successMessage}</Typography>}
      </Box>
      
      <Typography variant="h6" sx={{ mt: 4 }}>Existing Courses</Typography>
      <List>
        {courses.map(course => (
          <ListItem key={course.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography>{course.name}</Typography>
            <Box>
              <Button variant="outlined" onClick={() => navigate(`/mentor/edit-course/${course.id}`)} sx={{ ml: 2, mr: 2 }}>Edit</Button>
              <Button variant="contained" onClick={() => handleMakeTest(course.name)} sx={{ ml: 2, mr: 1 }}>Create Test</Button>
              <Button variant="contained" component="label" sx={{ ml: 2, mr: 2 }}>
                Upload Signature
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleSignatureChange(course.name)}
                />
              </Button>
              <Button 
                variant="contained" 
                onClick={() => navigate(`/mentor/record-content/${course.id}`)} 
                sx={{ ml: 2, mr: 2 }}
              >
                Record Content
              </Button>
            </Box>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default MentorPage;
